import { AppContext } from '@prisjakt/ui-app-context';
import serialize from 'form-serialize';
import React, { useCallback, useContext, useReducer } from 'react';
import { trackNewsletterSignup } from './tracking';

type State = {
  email: string;
  signupFeedbackTranslationKey?:
    | 'newsletter.sign-up-success'
    | 'newsletter.sign-up-error';
  isSubscribed: boolean;
  loading: boolean;
};

export const useNewsletterSignup = () => {
  const [
    { email, signupFeedbackTranslationKey, isSubscribed, loading },
    setState,
  ] = useReducer(
    (state: State, newState: Partial<State>) => ({
      ...state,
      ...newState,
    }),
    {
      email: '',
      isSubscribed: false,
      loading: false,
    }
  );

  const { market, renderMode } = useContext(AppContext);

  const subscribeNewsletter = useCallback(
    (subscriptionEmail: string) =>
      fetch(`/_internal/newsletter/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: subscriptionEmail }),
      }).then(res => res.json()),
    []
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement> | undefined) => {
      if (!e) {
        return;
      }
      const formData = serialize(e.target, { hash: true });

      e.preventDefault();

      if (formData.email) {
        setState({ loading: true });
        subscribeNewsletter(formData.email)
          .then(res => {
            setState({
              isSubscribed: res.success,
              signupFeedbackTranslationKey: res.success
                ? 'newsletter.sign-up-success'
                : 'newsletter.sign-up-error',
              loading: false,
            });

            if (res.success) {
              trackNewsletterSignup();
            }
          })
          .catch(() => {
            setState({
              signupFeedbackTranslationKey: 'newsletter.sign-up-error',
              loading: false,
            });
          });
      }
    },
    [subscribeNewsletter]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
      if (!event) {
        return;
      }
      setState({ [event.target.name]: event.target.value });
    },
    []
  );

  const privacyPolicyUri = (
    market?.privacySettings?.[renderMode] || market?.privacySettings?.responsive
  ).privacyUrl;

  return {
    state: { email, signupFeedbackTranslationKey, isSubscribed, loading },
    handleSubmit,
    handleChange,
    privacyPolicyUri,
  };
};
